import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, Shield, Sparkles, Activity, Users, Star, Target, Brain, ClipboardCheck, CheckCircle2, Phone, Mail, MapPin, Send, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6,
    ease: 'easeOut'
  }
};
export function Home() {
  const clients = [{
    title: 'Individuals & Families',
    desc: 'Comprehensive support for all ages and life stages.',
    img: 'https://cdn.magicpatterns.com/uploads/myZ3pXibnVme5ynPkMSgKE/Individuals_-_fAMILIES.png',
    bg: 'bg-[#E8F3EE]',
    tag: 'All Ages'
  }, {
    title: 'Youth & Adolescents',
    desc: 'Specialized care for the unique needs of young people.',
    img: 'https://cdn.magicpatterns.com/uploads/uNmrAPw2PQVKP3oFtMoBrX/Youth__Adolescents.png',
    bg: 'bg-[#BEF264]',
    tag: 'Youth'
  }, {
    title: 'Couples & Partners',
    desc: 'Restoring connection and rebuilding relationship foundations.',
    img: 'https://cdn.magicpatterns.com/uploads/8k4yLnxUhwk7hyJbbQViL4/Couples__partners.png',
    bg: 'bg-[#F0EEF9]',
    tag: 'Relationships'
  }, {
    title: 'Healthcare Patients',
    desc: 'Integrated medical and wellness care for recovery.',
    img: 'https://cdn.magicpatterns.com/uploads/tDpvm1x4bUSX1p1PDqUwmQ/Healthcare_Patients.webp',
    bg: 'bg-[#FFF4E8]',
    tag: 'Medical'
  }, {
    title: 'Veterans & Survivors',
    desc: 'Trauma-informed care for those who have served.',
    img: 'https://cdn.magicpatterns.com/uploads/tqC1UGg5VAZcmT3j6agrHY/Veterans__Trauma_Survivors.webp',
    bg: 'bg-[#E0E4DE]',
    tag: 'Specialized'
  }];
  const values = [{
    title: 'Compassion',
    desc: 'We lead with heart, ensuring every individual feels seen, heard, and deeply cared for.',
    color: 'bg-[#E8F3EE]',
    span: 'col-span-1',
    shape: 'rounded-br-[100px]'
  }, {
    title: 'Integrity & Excellence',
    desc: 'Our commitment to the highest clinical standards and ethical practices is unwavering in everything we do.',
    color: 'bg-[#F0EEF9]',
    span: 'md:col-span-2',
    shape: 'rounded-bl-[100px]'
  }, {
    title: 'Confidentiality',
    desc: 'Your trust is our foundation. We maintain the strictest privacy standards for all our clients.',
    color: 'bg-[#FFF4E8]',
    span: 'md:col-span-2',
    shape: 'rounded-tr-[100px]'
  }, {
    title: 'Empowerment',
    desc: 'We provide the tools and support necessary for you to take charge of your own restoration.',
    color: 'bg-[#FEF9E5]',
    span: 'col-span-1',
    shape: 'rounded-tl-[100px]'
  }, {
    title: 'Respect',
    desc: 'Valuing the unique journey and dignity of every person we serve.',
    color: 'bg-[#E8F3EE]',
    span: 'col-span-1',
    shape: 'rounded-br-[100px]'
  }, {
    title: 'Professionalism',
    desc: 'Expert care delivered with consistency and dedication.',
    color: 'bg-[#F0EEF9]',
    span: 'md:col-span-2',
    shape: 'rounded-bl-[100px]'
  }];
  return <main className="pt-32 md:pt-20" data-id="element-222">
      {/* 1. Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-medical-cream py-16" data-id="element-223">
        <div className="absolute inset-0 z-0" data-id="element-224">
          <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/a7nPyieMtULt6P6XsE72FP/ChatGPT_Image_Apr_8,_2026,_06_56_55_PM.png')] bg-cover bg-center opacity-[0.06]" data-id="element-225" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 w-full grid lg:grid-cols-2 gap-16 items-center" data-id="element-226">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-227">
            <span className="text-medical-red font-display text-sm uppercase tracking-widest mb-6 block" data-id="element-228">
              Global Restorative Health & Wellness
            </span>
            <h1 className="text-5xl md:text-7xl font-heading font-extrabold text-medical-blue leading-[1.1] mb-8" data-id="element-229">
              Restoring Health. <br data-id="element-230" />
              <span className="text-medical-red italic" data-id="element-231">
                Renewing Minds.
              </span>{' '}
              <br data-id="element-232" />
              Rebuilding Lives.
            </h1>
            <p className="text-lg md:text-xl text-medical-blue/70 mb-10 leading-relaxed font-body max-w-lg" data-id="element-233">
              A comprehensive healthcare and mental wellness organization
              dedicated to delivering holistic, compassionate, and
              evidence-based services.
            </p>
            <Link to="/about" className="inline-block bg-medical-red text-white px-10 py-5 rounded-full font-display font-bold text-lg hover:bg-medical-blue transition-all shadow-xl shadow-medical-red/20" data-id="element-234">
              LEARN MORE ABOUT US
            </Link>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="bg-white p-8 md:p-10 rounded-[32px] shadow-2xl border border-medical-gray/30" data-id="element-235">
            <h3 className="text-2xl font-heading text-medical-blue mb-2" data-id="element-236">
              Request an Appointment
            </h3>
            <p className="text-medical-blue/60 font-body text-sm mb-8" data-id="element-237">
              Schedule your free 20-minute consultation today.
            </p>
            <form className="space-y-5" data-id="element-238">
              <div className="grid grid-cols-2 gap-4" data-id="element-239">
                <div className="space-y-1" data-id="element-240">
                  <label className="text-xs font-display uppercase tracking-widest text-medical-blue/70" data-id="element-241">
                    First Name
                  </label>
                  <input type="text" className="w-full bg-medical-cream border border-medical-gray/50 rounded-xl px-4 py-3 focus:outline-none focus:border-medical-red font-body text-sm" placeholder="John" data-id="element-242" />
                </div>
                <div className="space-y-1" data-id="element-243">
                  <label className="text-xs font-display uppercase tracking-widest text-medical-blue/70" data-id="element-244">
                    Last Name
                  </label>
                  <input type="text" className="w-full bg-medical-cream border border-medical-gray/50 rounded-xl px-4 py-3 focus:outline-none focus:border-medical-red font-body text-sm" placeholder="Doe" data-id="element-245" />
                </div>
              </div>
              <div className="space-y-1" data-id="element-246">
                <label className="text-xs font-display uppercase tracking-widest text-medical-blue/70" data-id="element-247">
                  Email
                </label>
                <input type="email" className="w-full bg-medical-cream border border-medical-gray/50 rounded-xl px-4 py-3 focus:outline-none focus:border-medical-red font-body text-sm" placeholder="john@example.com" data-id="element-248" />
              </div>
              <div className="space-y-1" data-id="element-249">
                <label className="text-xs font-display uppercase tracking-widest text-medical-blue/70" data-id="element-250">
                  Phone
                </label>
                <input type="tel" className="w-full bg-medical-cream border border-medical-gray/50 rounded-xl px-4 py-3 focus:outline-none focus:border-medical-red font-body text-sm" placeholder="(555) 000-0000" data-id="element-251" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-252">
                <div className="space-y-1" data-id="element-253">
                  <label className="text-xs font-display uppercase tracking-widest text-medical-blue/70" data-id="element-254">
                    Preferred Date
                  </label>
                  <input type="date" className="w-full bg-medical-cream border border-medical-gray/50 rounded-xl px-4 py-3 focus:outline-none focus:border-medical-red font-body text-sm text-medical-blue" data-id="element-255" />
                </div>
                <div className="space-y-1" data-id="element-256">
                  <label className="text-xs font-display uppercase tracking-widest text-medical-blue/70" data-id="element-257">
                    Service Interest
                  </label>
                  <select className="w-full bg-medical-cream border border-medical-gray/50 rounded-xl px-4 py-3 focus:outline-none focus:border-medical-red font-body text-sm text-medical-blue" data-id="element-258">
                    <option data-id="element-259">Medical Wellness</option>
                    <option data-id="element-260">Mental Health Coaching</option>
                    <option data-id="element-261">Consultation</option>
                  </select>
                </div>
              </div>
              <button className="w-full bg-medical-red text-white py-4 rounded-xl font-display font-bold text-lg uppercase tracking-widest hover:bg-medical-blue transition-all mt-4" data-id="element-262">
                Request Appointment
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* 2. Our Specialization (About Excerpt) */}
      <section className="relative bg-medical-cream pt-20 pb-16" data-id="element-263">
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-[0] -translate-y-[99%]" data-id="element-264">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-[80px] fill-medical-cream" data-id="element-265">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.23,107.63,128.3,108.13,201.33,101.86,242.71,98.3,282.73,89.14,321.39,56.44Z" data-id="element-266"></path>
          </svg>
        </div>

        <div className="max-w-5xl mx-auto px-6 text-center" data-id="element-267">
          <motion.div {...fadeInUp} data-id="element-268">
            <span className="text-medical-red font-display text-xs uppercase tracking-widest mb-6 block" data-id="element-269">
              Our Specialization
            </span>
            <h2 className="text-3xl md:text-5xl font-heading text-medical-blue mb-10 leading-tight" data-id="element-270">
              Global Restorative Health & Wellness LLC is a comprehensive
              healthcare and mental wellness provider dedicated to helping
              individuals and families restore their health, rebuild emotional
              strength, and rediscover purpose.
            </h2>
            <Link to="/about" className="inline-block bg-medical-red text-white px-10 py-4 rounded-full font-display font-bold text-sm hover:bg-medical-blue transition-all" data-id="element-271">
              LEARN MORE
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-medical-blue text-white" data-id="element-272">
        <div className="max-w-7xl mx-auto px-6" data-id="element-273">
          <div className="text-center mb-20" data-id="element-274">
            <h2 className="text-3xl md:text-5xl font-heading italic text-white" data-id="element-275">
              Our Approach
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-12 text-center" data-id="element-276">
            {[{
            title: 'Prevention',
            desc: 'Proactive strategies to maintain health and prevent disease before it starts.'
          }, {
            title: 'Restoration',
            desc: 'Helping you regain control and restore balance to your body and mind.'
          }, {
            title: 'Empowerment',
            desc: 'Giving you the tools and knowledge to take charge of your wellness journey.'
          }, {
            title: 'Whole-person care',
            desc: 'Addressing the physical, emotional, and psychological needs of every client.'
          }].map((item, i) => <motion.div key={i} {...fadeInUp} transition={{
            delay: i * 0.1
          }} data-id="element-277">
                <Star className="mx-auto mb-8 text-medical-red" size={48} data-id="element-278" />
                <h3 className="text-2xl md:text-3xl font-heading mb-6" data-id="element-279">
                  {item.title}
                </h3>
                <p className="opacity-60 leading-relaxed font-body" data-id="element-280">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 3. Our Services */}
      <section className="py-20 bg-medical-gray/20 overflow-hidden" data-id="element-281">
        <div className="bg-medical-blue py-8 mb-20" data-id="element-282">
          <div className="marquee" data-id="element-283">
            <div className="marquee-content" data-id="element-284">
              {[1, 2, 3, 4, 5].map(i => <span key={i} className="text-4xl md:text-6xl font-heading text-white/20 uppercase tracking-[0.2em] flex items-center gap-12" data-id="element-285">
                  OUR SERVICES{' '}
                  <Star fill="currentColor" size={24} className="text-medical-red" data-id="element-286" />
                </span>)}
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 space-y-12" data-id="element-287">
          {[{
          title: 'Family Practice & Lifestyle Wellness',
          desc: 'Comprehensive primary care integrated with lifestyle counseling to promote total well-being.',
          img: 'https://cdn.magicpatterns.com/uploads/eweKbPKtMkqAK2acxzaGGp/Individuals__Families.webp',
          num: '01',
          bg: '#B7CCE3',
          darkText: true
        }, {
          title: 'Health Risk Assessments (HRAs)',
          desc: 'Detailed evaluations to identify risks and create personalized preventive health plans.',
          img: 'https://cdn.magicpatterns.com/uploads/41mmTjpjeUo4EuVuLHcpnf/Health_Risk_Assessments_(HRAs).jpg',
          num: '02',
          bg: '#721010',
          darkText: false
        }, {
          title: 'Critical Care Consultation',
          desc: 'Clinical consultation and care coordination with post-hospital recovery support.',
          img: 'https://cdn.magicpatterns.com/uploads/tqC1UGg5VAZcmT3j6agrHY/Veterans__Trauma_Survivors.webp',
          num: '03',
          bg: '#E0E4DE',
          darkText: true
        }, {
          title: 'Mental Health Coaching Services',
          desc: 'Specialized coaching for marriage, trauma recovery, anxiety, and life transitions.',
          img: 'https://cdn.magicpatterns.com/uploads/e4LVpLJpYC1KKKciqBgn3q/Mental_Health_Coaching_Services.webp',
          num: '04',
          bg: '#BCBDB8',
          darkText: true
        }, {
          title: 'Consultation & Professional Training',
          desc: 'Expert healthcare consultation and organizational wellness programs for teams.',
          img: 'https://cdn.magicpatterns.com/uploads/caZWkBoY8SixaQKEBiArrV/Consultation__Professional_Training.jpg',
          num: '05',
          bg: '#8DA1B9',
          darkText: true
        }].map((service, i) => <motion.div key={i} {...fadeInUp} className="p-8 md:p-12 rounded-[40px] flex flex-col md:flex-row gap-12 items-center relative overflow-hidden group shadow-2xl" style={{
          backgroundColor: service.bg
        }} data-id="element-288">
              <span className={`absolute top-8 right-12 text-xs font-display opacity-40 ${service.darkText ? 'text-medical-blue' : 'text-white'}`} data-id="element-289">
                {service.num}
              </span>
              <div className="flex-1" data-id="element-290">
                <h3 className={`text-3xl md:text-4xl font-heading mb-6 ${service.darkText ? 'text-medical-blue' : 'text-white'}`} data-id="element-291">
                  {service.title}
                </h3>
                <p className={`mb-10 leading-relaxed text-lg font-body ${service.darkText ? 'text-medical-blue/60' : 'text-white/60'}`} data-id="element-292">
                  {service.desc}
                </p>
                <Link to="/services" className={`text-xs font-display uppercase tracking-widest border-b pb-1 transition-all ${service.darkText ? 'text-medical-blue border-medical-red hover:text-medical-red' : 'text-white border-medical-red hover:text-medical-red'}`} data-id="element-293">
                  → EXPLORE {service.title.toUpperCase()}
                </Link>
              </div>
              <div className="w-full md:w-2/5 aspect-video md:aspect-square rounded-3xl overflow-hidden shadow-2xl" data-id="element-294">
                <img src={service.img} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-295" />
              </div>
            </motion.div>)}
        </div>

        <div className="text-center mt-16" data-id="element-296">
          <Link to="/services" className="inline-flex items-center gap-3 bg-medical-red text-white px-12 py-5 rounded-full font-display font-bold text-lg uppercase tracking-widest hover:bg-medical-blue transition-all shadow-xl shadow-medical-red/20" data-id="element-297">
            View All Services <ArrowRight size={20} data-id="element-298" />
          </Link>
        </div>
      </section>

      {/* 4. Who We Serve */}
      <section className="py-20 bg-medical-cream/30 overflow-hidden" data-id="element-299">
        <div className="max-w-7xl mx-auto px-6" data-id="element-300">
          <div className="flex justify-between items-end mb-16" data-id="element-301">
            <div data-id="element-302">
              <span className="text-medical-red font-display text-xs uppercase tracking-widest mb-4 block" data-id="element-303">
                OUR CLIENTS
              </span>
              <h2 className="text-4xl md:text-6xl font-heading text-medical-blue" data-id="element-304">
                Who We Serve
              </h2>
            </div>
            <div className="flex gap-4" data-id="element-305">
              <button onClick={() => {
              const el = document.getElementById('clients-slider');
              if (el) el.scrollBy({
                left: -400,
                behavior: 'smooth'
              });
            }} className="w-12 h-12 rounded-full border border-medical-blue text-medical-blue flex items-center justify-center hover:bg-medical-blue hover:text-white transition-all" aria-label="Scroll left" data-id="element-306">
                <ArrowRight size={20} className="rotate-180" data-id="element-307" />
              </button>
              <button onClick={() => {
              const el = document.getElementById('clients-slider');
              if (el) el.scrollBy({
                left: 400,
                behavior: 'smooth'
              });
            }} className="w-12 h-12 rounded-full bg-medical-blue text-white flex items-center justify-center hover:bg-medical-red transition-all" aria-label="Scroll right" data-id="element-308">
                <ArrowRight size={20} data-id="element-309" />
              </button>
            </div>
          </div>

          <div id="clients-slider" className="flex overflow-x-auto gap-6 pb-12 no-scrollbar snap-x scroll-smooth" data-id="element-310">
            {clients.map((client, i) => <motion.div key={i} className="min-w-[300px] md:min-w-[380px] snap-start bg-white rounded-[40px] overflow-hidden shadow-xl flex flex-col group" data-id="element-311">
                <div className={`${client.bg} p-10 flex-1 flex flex-col`} data-id="element-312">
                  <div className="flex gap-2 mb-8" data-id="element-313">
                    <span className="bg-white/50 backdrop-blur-sm px-4 py-1.5 rounded-full text-[10px] font-display uppercase tracking-widest text-medical-blue" data-id="element-314">
                      {client.tag}
                    </span>
                  </div>
                  <h3 className="text-4xl font-heading text-medical-blue mb-4 leading-tight" data-id="element-315">
                    {client.title}
                  </h3>
                  <p className="text-medical-blue/60 font-body text-sm leading-relaxed" data-id="element-316">
                    {client.desc}
                  </p>
                </div>
                <div className="h-[300px] relative overflow-hidden" data-id="element-317">
                  <img src={client.img} alt={client.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-318" />
                  <div className="absolute bottom-6 left-6 right-6" data-id="element-319">
                    <button className="w-full bg-white/90 backdrop-blur-md py-4 rounded-2xl flex items-center justify-center gap-3 font-display text-xs uppercase tracking-widest text-medical-blue hover:bg-medical-red hover:text-white transition-all" data-id="element-320">
                      Read More{' '}
                      <ArrowRight size={14} className="bg-medical-blue text-white rounded-full p-0.5" data-id="element-321" />
                    </button>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 5. Our Competitive Advantage (Why Choose Us) */}
      <section className="py-20 bg-white" data-id="element-322">
        <div className="max-w-7xl mx-auto px-6" data-id="element-323">
          <div className="grid lg:grid-cols-2 gap-20 items-center" data-id="element-324">
            <motion.div {...fadeInUp} data-id="element-325">
              <span className="text-medical-red font-display text-xs uppercase tracking-widest mb-6 block" data-id="element-326">
                Why Choose Us
              </span>
              <h2 className="text-4xl md:text-6xl font-heading text-medical-blue mb-8" data-id="element-327">
                The Global Restorative{' '}
                <span className="text-medical-red italic" data-id="element-328">Advantage</span>
              </h2>
              <div className="grid gap-6" data-id="element-329">
                {[{
                title: 'Integrated Medical & Mental Wellness',
                desc: 'A unique combination of family medical care and professional mental health coaching.'
              }, {
                title: 'Experienced NP Leadership',
                desc: 'Led by a Nurse Practitioner with over 15 years of clinical medical experience.'
              }, {
                title: 'Certified Professional Coaching',
                desc: 'Board-Certified Master Mental Health Coach with trauma-informed expertise.'
              }, {
                title: 'Client-Centered Approach',
                desc: 'Compassionate, confidential, and personalized services tailored to your needs.'
              }].map((item, i) => <div key={i} className="flex gap-4" data-id="element-330">
                    <div className="w-10 h-10 rounded-full bg-medical-red/10 flex items-center justify-center shrink-0 text-medical-red" data-id="element-331">
                      <CheckCircle2 size={20} data-id="element-332" />
                    </div>
                    <div data-id="element-333">
                      <h4 className="font-heading font-bold text-medical-blue text-xl mb-1" data-id="element-334">
                        {item.title}
                      </h4>
                      <p className="text-medical-blue/60 font-body" data-id="element-335">
                        {item.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </motion.div>
            <div className="relative" data-id="element-336">
              <div className="rounded-[60px] overflow-hidden shadow-2xl border-8 border-medical-cream aspect-square" data-id="element-337">
                <img src="https://cdn.magicpatterns.com/uploads/kwpy3pSbunAaXqHD4LqUE2/global_1.png" alt="Why Choose Us" className="w-full h-full object-cover" data-id="element-338" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Our Core Values */}
      <section className="py-20 bg-white" data-id="element-339">
        <div className="max-w-7xl mx-auto px-6" data-id="element-340">
          <div className="text-center mb-16" data-id="element-341">
            <span className="text-medical-red font-display text-xs uppercase tracking-widest mb-4 block" data-id="element-342">
              What Drives Us
            </span>
            <h2 className="text-4xl md:text-6xl font-heading text-medical-blue" data-id="element-343">
              Our Core Values
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-344">
            {values.map((val, i) => <motion.div key={i} {...fadeInUp} transition={{
            delay: i * 0.1
          }} className={`${val.color} ${val.span} p-10 rounded-[40px] relative overflow-hidden min-h-[280px] flex flex-col justify-center group`} data-id="element-345">
                <div className={`absolute bottom-0 right-0 w-32 h-32 bg-black/5 ${val.shape} transition-transform duration-700 group-hover:scale-150`} data-id="element-346" />
                <div className="relative z-10" data-id="element-347">
                  <h3 className="text-3xl font-heading text-medical-blue mb-4" data-id="element-348">
                    {val.title}
                  </h3>
                  <p className="text-medical-blue/70 font-body leading-relaxed max-w-sm" data-id="element-349">
                    {val.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 7. Expertise Behind Your Restoration (Leadership) */}
      <section className="py-20 bg-white" data-id="element-350">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-20 items-center" data-id="element-351">
          <motion.div {...fadeInUp} data-id="element-352">
            <span className="text-medical-red font-display text-xs uppercase tracking-widest mb-6 block" data-id="element-353">
              OUR LEADERSHIP
            </span>
            <h2 className="text-4xl md:text-6xl font-heading text-medical-blue mb-8" data-id="element-354">
              Expertise Behind Your Restoration
            </h2>
            <p className="text-lg text-medical-blue/70 mb-10 leading-relaxed font-body" data-id="element-355">
              Our leadership team brings over 20 years of clinical medical
              experience and advanced expertise in mental health coaching,
              trauma recovery, and emotional wellness.
            </p>
            <Link to="/about" className="inline-flex items-center gap-2 text-medical-red font-display font-bold uppercase tracking-widest border-b-2 border-medical-red pb-1 hover:text-medical-blue transition-all" data-id="element-356">
              Learn more about our team <ArrowRight size={16} data-id="element-357" />
            </Link>
          </motion.div>

          <div className="flex gap-6 justify-center lg:justify-end" data-id="element-358">
            <div className="text-center" data-id="element-359">
              <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden shadow-2xl border-4 border-medical-cream mb-4 mx-auto" data-id="element-360">
                <img src="https://cdn.magicpatterns.com/uploads/a7nPyieMtULt6P6XsE72FP/ChatGPT_Image_Apr_8,_2026,_06_56_55_PM.png" alt="Founder" className="w-full h-full object-cover" data-id="element-361" />
              </div>
              <h4 className="font-heading font-bold text-medical-blue text-lg" data-id="element-362">
                Founder & Clinical Director
              </h4>
              <p className="text-xs font-display text-medical-red uppercase tracking-widest mt-1" data-id="element-363">
                Nurse Practitioner
              </p>
            </div>
            <div className="text-center mt-12" data-id="element-364">
              <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden shadow-2xl border-4 border-medical-cream mb-4 mx-auto" data-id="element-365">
                <img src="https://cdn.magicpatterns.com/uploads/j9UxNAHbLcjVrfMMUSd89S/global_2.png" alt="Co-Founder" className="w-full h-full object-cover" data-id="element-366" />
              </div>
              <h4 className="font-heading font-bold text-medical-blue text-lg" data-id="element-367">
                Co-Founder & Director
              </h4>
              <p className="text-xs font-display text-medical-red uppercase tracking-widest mt-1" data-id="element-368">
                Mental Health Coach
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. From Our Blog */}
      <section className="py-20 bg-white overflow-hidden" data-id="element-369">
        <div className="max-w-7xl mx-auto px-6" data-id="element-370">
          <div className="flex justify-between items-end mb-16" data-id="element-371">
            <div data-id="element-372">
              <span className="text-medical-red font-display text-xs uppercase tracking-[0.3em] mb-4 block" data-id="element-373">
                Latest Insights
              </span>
              <h2 className="text-4xl md:text-6xl font-heading text-medical-blue" data-id="element-374">
                From Our <span className="text-medical-red italic" data-id="element-375">Blog</span>
              </h2>
            </div>
            <div className="flex gap-4" data-id="element-376">
              <button onClick={() => {
              const el = document.getElementById('blog-slider');
              if (el) el.scrollBy({
                left: -400,
                behavior: 'smooth'
              });
            }} className="w-12 h-12 rounded-full border border-medical-blue text-medical-blue flex items-center justify-center hover:bg-medical-blue hover:text-white transition-all" aria-label="Scroll left" data-id="element-377">
                <ArrowRight size={20} className="rotate-180" data-id="element-378" />
              </button>
              <button onClick={() => {
              const el = document.getElementById('blog-slider');
              if (el) el.scrollBy({
                left: 400,
                behavior: 'smooth'
              });
            }} className="w-12 h-12 rounded-full bg-medical-blue text-white flex items-center justify-center hover:bg-medical-red transition-all" aria-label="Scroll right" data-id="element-379">
                <ArrowRight size={20} data-id="element-380" />
              </button>
            </div>
          </div>

          <div id="blog-slider" className="flex overflow-x-auto gap-6 pb-12 no-scrollbar snap-x scroll-smooth" data-id="element-381">
            {[{
            title: 'Understanding Holistic Wellness',
            category: 'Wellness',
            desc: 'Explore the connection between physical health and mental well-being.',
            img: 'https://cdn.magicpatterns.com/uploads/tDpvm1x4bUSX1p1PDqUwmQ/Healthcare_Patients.webp',
            bg: 'bg-[#F0EEF9]'
          }, {
            title: 'Navigating Trauma Recovery',
            category: 'Mental Health',
            desc: 'Steps toward rebuilding emotional strength and rediscovering purpose.',
            img: 'https://cdn.magicpatterns.com/uploads/e4LVpLJpYC1KKKciqBgn3q/Mental_Health_Coaching_Services.webp',
            bg: 'bg-[#E8F3EE]'
          }, {
            title: 'The Importance of HRAs',
            category: 'Prevention',
            desc: 'Why regular assessments are the cornerstone of proactive health.',
            img: 'https://cdn.magicpatterns.com/uploads/41mmTjpjeUo4EuVuLHcpnf/Health_Risk_Assessments_(HRAs).jpg',
            bg: 'bg-[#FFF4E8]'
          }].map((post, i) => <motion.div key={i} className="min-w-[300px] md:min-w-[380px] snap-start bg-white rounded-[40px] overflow-hidden shadow-xl flex flex-col group" data-id="element-382">
                <div className={`${post.bg} p-10 flex-1 flex flex-col`} data-id="element-383">
                  <div className="flex gap-2 mb-8" data-id="element-384">
                    <span className="bg-white/50 backdrop-blur-sm px-4 py-1.5 rounded-full text-[10px] font-display uppercase tracking-widest text-medical-blue" data-id="element-385">
                      {post.category}
                    </span>
                  </div>
                  <h3 className="text-4xl font-heading text-medical-blue mb-4 leading-tight" data-id="element-386">
                    {post.title}
                  </h3>
                  <p className="text-medical-blue/60 font-body text-sm leading-relaxed" data-id="element-387">
                    {post.desc}
                  </p>
                </div>
                <div className="h-[300px] relative overflow-hidden" data-id="element-388">
                  <img src={post.img} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-389" />
                  <div className="absolute bottom-6 left-6 right-6" data-id="element-390">
                    <Link to="/blog" className="w-full bg-white/90 backdrop-blur-md py-4 rounded-2xl flex items-center justify-center gap-3 font-display text-xs uppercase tracking-widest text-medical-blue hover:bg-medical-red hover:text-white transition-all" data-id="element-391">
                      Read Article{' '}
                      <ArrowRight size={14} className="bg-medical-blue text-white rounded-full p-0.5" data-id="element-392" />
                    </Link>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 9. Testimonials */}
      <section className="py-20 bg-medical-blue text-white overflow-hidden relative" data-id="element-393">
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-[0] rotate-180" data-id="element-394">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-[60px] fill-white" data-id="element-395">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.23,107.63,128.3,108.13,201.33,101.86,242.71,98.3,282.73,89.14,321.39,56.44Z" data-id="element-396"></path>
          </svg>
        </div>
        <div className="max-w-7xl mx-auto px-6 pt-16" data-id="element-397">
          <div className="text-center mb-20" data-id="element-398">
            <span className="text-medical-red font-display text-xs uppercase tracking-widest mb-4 block" data-id="element-399">
              Testimonials
            </span>
            <h2 className="text-4xl md:text-6xl font-heading italic text-white" data-id="element-400">
              Stories of Transformation
            </h2>
          </div>
          <div className="flex overflow-x-auto gap-8 pb-12 no-scrollbar snap-x" data-id="element-401">
            {[1, 2, 3].map(i => <motion.div key={i} className="min-w-[320px] md:min-w-[450px] snap-start bg-white/5 border border-white/10 p-10 rounded-[32px]" data-id="element-402">
                <Quote className="text-medical-red mb-6" size={32} data-id="element-403" />
                <p className="font-migra text-xl md:text-2xl leading-relaxed mb-8 text-white/90" data-id="element-404">
                  "Global Restorative completely changed my approach to
                  wellness. The integrated care model helped me address both my
                  physical symptoms and underlying stress. I finally feel like
                  myself again."
                </p>
                <div className="flex items-center gap-4" data-id="element-405">
                  <div className="w-12 h-12 bg-medical-red/20 rounded-full flex items-center justify-center text-medical-red font-display font-bold" data-id="element-406">
                    {['JD', 'SM', 'RJ'][i - 1]}
                  </div>
                  <div data-id="element-407">
                    <h4 className="font-heading font-bold text-lg" data-id="element-408">
                      Client Name
                    </h4>
                    <p className="text-sm font-display text-medical-red uppercase tracking-widest" data-id="element-409">
                      Wellness Program
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0]" data-id="element-410">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-[60px] fill-white" data-id="element-411">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.23,107.63,128.3,108.13,201.33,101.86,242.71,98.3,282.73,89.14,321.39,56.44Z" data-id="element-412"></path>
          </svg>
        </div>
      </section>

      {/* 10. Get In Touch */}
      <section className="py-20 bg-medical-blue text-white relative overflow-hidden" data-id="element-413">
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-[0] rotate-180" data-id="element-414">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-[60px] fill-white" data-id="element-415">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.23,107.63,128.3,108.13,201.33,101.86,242.71,98.3,282.73,89.14,321.39,56.44Z" data-id="element-416"></path>
          </svg>
        </div>
        <div className="max-w-7xl mx-auto px-6 pt-16 grid lg:grid-cols-2 gap-20 items-center" data-id="element-417">
          <motion.div {...fadeInUp} data-id="element-418">
            <span className="text-medical-red font-display text-xs uppercase tracking-widest mb-6 block" data-id="element-419">
              Contact Us
            </span>
            <h2 className="text-4xl md:text-6xl font-heading mb-8" data-id="element-420">
              Get In Touch
            </h2>
            <p className="text-lg opacity-80 mb-12 font-body leading-relaxed" data-id="element-421">
              Ready to begin your restorative journey? Contact us today to
              schedule your consultation or ask any questions about our
              integrated services.
            </p>
            <div className="space-y-8" data-id="element-422">
              <div className="flex items-center gap-6" data-id="element-423">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-medical-red" data-id="element-424">
                  <Phone size={20} data-id="element-425" />
                </div>
                <div data-id="element-426">
                  <p className="text-xs font-display uppercase tracking-widest opacity-60 mb-1" data-id="element-427">
                    Call Us
                  </p>
                  <p className="text-xl font-heading" data-id="element-428">+1 (555) 000-0000</p>
                </div>
              </div>
              <div className="flex items-center gap-6" data-id="element-429">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-medical-red" data-id="element-430">
                  <Mail size={20} data-id="element-431" />
                </div>
                <div data-id="element-432">
                  <p className="text-xs font-display uppercase tracking-widest opacity-60 mb-1" data-id="element-433">
                    Email Us
                  </p>
                  <p className="text-xl font-heading" data-id="element-434">
                    info@globalrestorative.com
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-6" data-id="element-435">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-medical-red" data-id="element-436">
                  <MapPin size={20} data-id="element-437" />
                </div>
                <div data-id="element-438">
                  <p className="text-xs font-display uppercase tracking-widest opacity-60 mb-1" data-id="element-439">
                    Location
                  </p>
                  <p className="text-xl font-heading" data-id="element-440">Telehealth Worldwide</p>
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div {...fadeInUp} className="bg-white p-10 rounded-[40px] shadow-2xl text-medical-blue" data-id="element-441">
            <h3 className="text-2xl font-heading mb-8" data-id="element-442">Send a Message</h3>
            <form className="space-y-6" data-id="element-443">
              <div className="grid md:grid-cols-2 gap-6" data-id="element-444">
                <div className="space-y-2" data-id="element-445">
                  <label className="text-xs font-display uppercase tracking-widest text-medical-blue/70" data-id="element-446">
                    Name
                  </label>
                  <input type="text" className="w-full bg-medical-cream border border-medical-gray/50 rounded-xl px-4 py-3 focus:outline-none focus:border-medical-red font-body text-sm" placeholder="Your Name" data-id="element-447" />
                </div>
                <div className="space-y-2" data-id="element-448">
                  <label className="text-xs font-display uppercase tracking-widest text-medical-blue/70" data-id="element-449">
                    Phone
                  </label>
                  <input type="tel" className="w-full bg-medical-cream border border-medical-gray/50 rounded-xl px-4 py-3 focus:outline-none focus:border-medical-red font-body text-sm" placeholder="Your Phone" data-id="element-450" />
                </div>
              </div>
              <div className="space-y-2" data-id="element-451">
                <label className="text-xs font-display uppercase tracking-widest text-medical-blue/70" data-id="element-452">
                  Email
                </label>
                <input type="email" className="w-full bg-medical-cream border border-medical-gray/50 rounded-xl px-4 py-3 focus:outline-none focus:border-medical-red font-body text-sm" placeholder="Your Email" data-id="element-453" />
              </div>
              <div className="space-y-2" data-id="element-454">
                <label className="text-xs font-display uppercase tracking-widest text-medical-blue/70" data-id="element-455">
                  Message
                </label>
                <textarea rows={4} className="w-full bg-medical-cream border border-medical-gray/50 rounded-xl px-4 py-3 focus:outline-none focus:border-medical-red font-body text-sm" placeholder="How can we help you?" data-id="element-456" />
              </div>
              <button className="w-full bg-medical-red text-white py-4 rounded-xl font-display font-bold text-lg uppercase tracking-widest hover:bg-medical-blue transition-all flex items-center justify-center gap-2" data-id="element-457">
                Send Message <Send size={18} data-id="element-458" />
              </button>
            </form>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0]" data-id="element-459">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-[60px] fill-medical-blue" data-id="element-460">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.23,107.63,128.3,108.13,201.33,101.86,242.71,98.3,282.73,89.14,321.39,56.44Z" className="fill-medical-blue" data-id="element-461"></path>
          </svg>
        </div>
      </section>
    </main>;
}